import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // El radio del botón es propio (`--radius-btn`, 9px) y no sale de `--radius`:
  // el botón es más cerrado que cualquier superficie, y bajar `--radius` para
  // conseguirlo movería tarjetas y diálogos.
  //
  // `active:scale-[.97]` es UNA de las dos excepciones sancionadas a «la
  // presión no desplaza» (la otra es el levantamiento de la tarjeta de opción).
  // Va acá, en la clase base del rol, y no en ningún sitio de uso — el
  // presupuesto de `active:scale` está en 1.
  "inline-flex shrink-0 items-center justify-center rounded-btn font-medium whitespace-nowrap transition-all duration-fast outline-none motion-safe:active:scale-[.97] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Gradiente + filete + halo, no un plano de color: el plano a plena luz
        // era lo que hacía que el botón se leyera como un resaltador.
        default: "dd-btn-primary",
        // En oscuro el destructivo es borde y tinte, no un bloque rojo lleno.
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:border dark:border-err/30 dark:bg-err/10 dark:text-err dark:hover:bg-err/16 dark:focus-visible:ring-destructive/40",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-border dark:bg-fg/5 dark:hover:bg-fg/9",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:text-fg-muted dark:hover:bg-fg/6 dark:hover:text-fg",
        link: "text-primary underline-offset-4 hover:underline",
      },
      // Cada tamaño trae alto, padding, separación, cuerpo de texto y tamaño de
      // ícono. Antes el cuerpo salía de la clase base y el alto de acá, así que
      // el sitio de uso que quería otro cuerpo tenía que escribir también el
      // alto — y 59 botones terminaron escribiendo su tamaño a mano en nueve
      // combinaciones distintas. La más usada de todas, `h-7`, NO existía como
      // variante: por eso la escribían. Existe.
      size: {
        xs: "h-6 gap-1 px-2 text-micro has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1.5 px-2.5 text-caption has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
        default: "h-8 gap-2 px-3 text-body has-[>svg]:px-2.5 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-9 gap-2 px-4 text-body-lg has-[>svg]:px-3.5 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-8 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-9 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
